import type { CSSProperties, ReactElement } from 'react'
import { Component, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { useSwipeable } from 'react-swipeable'
import cc from 'classcat'

import { measureBarsHeight } from '../../../utils/measureBarsHeight'
import { switchLanguage } from '../../../store/actions'
import { useTranslate } from '../../../utils/translate'
import Link from '../../templateComponents/Link'
import useLanguageSwitchLocales from '../../../utils/hooks/useLanguageSwitchLocales'

type MenuItemProps = Readonly<{
  item: Frontend.NestedPage
  selectedItem: Frontend.NestedPage
}>

type MenuItemState = {
  isOpen: boolean
}

export class MenuItem extends Component<MenuItemProps, MenuItemState> {
  private subMenu: HTMLUListElement

  state = {
    isOpen: isMenuItemOpen(this.props),
  }

  UNSAFE_componentWillReceiveProps(nextProps: MenuItemProps) {
    this.setState({
      isOpen: isMenuItemOpen(nextProps),
    })
  }

  componentDidMount() {
    if (this.subMenu && this.state.isOpen) this.openSubMenu()
  }

  componentDidUpdate(_prevProps: MenuItemProps, prevState: MenuItemState) {
    if (this.subMenu) {
      if (!prevState.isOpen && this.state.isOpen) {
        this.openSubMenu()
      } else if (prevState.isOpen && !this.state.isOpen) {
        this.closeSubMenu()
      }
    }
  }

  openSubMenu() {
    // 1. Transition to inner content's height
    // 2. Set height to `auto` once the transition ended
    this.subMenu.style.height = `${this.subMenu.scrollHeight}px`
    this.subMenu.addEventListener('transitionend', this.handleTransitionEnd)
  }

  closeSubMenu() {
    // 1. Temporarily disable the transition effect and explicitly set the current
    //    pixel height value of the sub menu in order for the transition effect to
    //    work (can't transition out of `auto`)
    // 2. Transition the sub menu's height to `0px`
    this.subMenu.classList.add('notransition')
    window.requestAnimationFrame(() => {
      if (this.subMenu) {
        this.subMenu.style.height = `${this.subMenu.scrollHeight}px`
        this.subMenu.classList.remove('notransition')
      }
      window.requestAnimationFrame(() => {
        if (this.subMenu) {
          this.subMenu.style.height = '0'
        }
      })
    })
  }

  handleTransitionEnd = () => {
    this.subMenu.removeEventListener('transitionend', this.handleTransitionEnd)
    if (this.subMenu && this.state.isOpen) this.subMenu.style.height = 'auto'
  }

  render() {
    const { item, selectedItem } = this.props
    const { isOpen } = this.state
    const hasSubMenu = item.children.length > 0

    if (item.href === '/' && (!item.isVisible || (item.type === 'category' && !item.visibleInNavigation))) {
      return null
    }

    return (
      <li
        className={cc({
          'has-sub-menu': hasSubMenu,
          active: item.isCurrentPage,
        })}
      >
        <Link to={item.href}>{item.title}</Link>
        {hasSubMenu && (
          <>
            <div onClick={() => this.setState({ isOpen: !isOpen })} className={cc(['drop-icon', { opened: isOpen }])} />
            <ul
              ref={(node: HTMLUListElement) => {
                this.subMenu = node
              }}
              className="sub-menu"
            >
              {item.children.map((child) => (
                <MenuItem selectedItem={selectedItem} key={child.id} item={child} />
              ))}
            </ul>
          </>
        )}
      </li>
    )
  }
}

const openClass = 'js-page-menu-open'
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '])

type OffCanvasProps = {
  className?: string
  direction?: 'left' | 'right'
  items: Frontend.NestedPage[]
}

export default function OffCanvas({ items, className, direction = 'left' }: Readonly<OffCanvasProps>): ReactElement {
  const dispatch = useDispatch<GlobalDispatch>()
  const themeName = useSelector<State, string>((state) => state.getIn(['shop', 'theme', 'name']))
  const selectedItem = useRef(findActiveItem(items))
  const [isOpen, setIsOpen] = useState(false)
  const [isHidden, setHidden] = useState(true)
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false)
  const [locales, shopLocale, languageSelectTranslations] = useLanguageSwitchLocales()
  const location = useLocation()

  const t = useTranslate('shop', 'components.storefrontMainMenuComponent')

  // Unhide the menu and manage scroll locking and scroll position preservation
  // while the menu is open.
  // Needed for mobile in general and for the Vision oneRow header in desktop,
  // which share the same OffCanvas implementation.
  useLayoutEffect(() => {
    if (!isOpen) {
      // Defer setting `visibility: hidden` to not hide the element in the
      // middle of the page menu slide out transition animation.
      // Keep value in sync with transition duration!
      // Also defer removing --bars-total-height so the menu wrapper keeps its
      // negative top offset throughout the slide-out and doesn't jump down.
      const timeoutId = setTimeout(() => {
        setHidden(true)
        document.body.style.removeProperty('--bars-total-height')
      }, 400)
      return () => {
        clearTimeout(timeoutId)
        document.body.style.removeProperty('--bars-total-height')
      }
    }

    const isMobileMenu = window.innerWidth < 992
    const shouldLockScroll = themeName?.startsWith('epages.vision') || isMobileMenu
    const menuWrapper = shouldLockScroll ? document.querySelector('#main-menu-offcanvas .main-menu-wrapper') : null
    let container: HTMLElement | null = null

    const preventTouchScroll = (e: TouchEvent) => {
      if (!menuWrapper?.contains(e.target as Node)) {
        e.preventDefault()
        return
      }
      // overscroll-behavior:contain only prevents scroll chaining on scrollable elements.
      // If the menu is not scrollable, block touch scroll manually.
      // Find the direct-child ul that contains the touch target (e.g. language menu vs main menu).
      const menuUls = menuWrapper.querySelectorAll<HTMLElement>(':scope > ul')
      const activeUl =
        Array.from(menuUls).find((ul) => ul.contains(e.target as Node)) ??
        menuWrapper.querySelector<HTMLElement>('ul.main-menu')
      if (!activeUl || activeUl.scrollHeight <= activeUl.clientHeight) e.preventDefault()
    }

    const preventWheelScroll = (e: WheelEvent) => {
      if (!menuWrapper?.contains(e.target as Node)) e.preventDefault()
    }

    const preventKeyScroll = (e: KeyboardEvent) => {
      if (SCROLL_KEYS.has(e.key) && !menuWrapper?.contains(document.activeElement)) e.preventDefault()
    }

    if (shouldLockScroll) {
      // When the header is partially scrolled out of view (non-sticky only), snap to the
      // top so the menu panel opens fully visible at the viewport edge instead of off-screen.
      const header = document.querySelector('.site-header')
      if (window.scrollY > 0 && !header?.classList.contains('site-header-sticky')) {
        window.scrollTo({ top: 0, behavior: 'instant' })
      }

      const scrollY = window.scrollY
      container = document.querySelector<HTMLElement>('.container')

      // Preserve scroll position, mainly relevant with sticky header enabled.
      // Override height:100vh on .container — without this, adding the openClass
      // clamps scrollY to 0 and the page jumps to the top.
      if (container) container.style.height = `${scrollY + window.innerHeight}px`

      // Suppress iOS rubber-band overscroll and prevent scrollbar drag in desktop browsers.
      document.documentElement.style.overscrollBehavior = 'none'
      document.documentElement.style.overflowY = 'hidden'

      // Contain menu scroll so it doesn't chain to the document.
      menuWrapper?.querySelectorAll<HTMLElement>(':scope > ul').forEach((ul) => {
        ul.style.overscrollBehavior = 'contain'
      })

      document.body.style.setProperty('--bars-total-height', `${measureBarsHeight()}px`)

      document.addEventListener('touchmove', preventTouchScroll, { passive: false })
      document.addEventListener('wheel', preventWheelScroll, { passive: false })
      document.addEventListener('keydown', preventKeyScroll)
    }

    // Unhide the menu after container height is set but before the open class.
    // Prevents both an iOS clip artifact and a WebKit transition flash.
    setHidden(false)
    const rafId = requestAnimationFrame(() => document.body.classList.add(openClass))

    return () => {
      cancelAnimationFrame(rafId)
      document.body.classList.remove(openClass)
      if (shouldLockScroll) {
        document.removeEventListener('touchmove', preventTouchScroll)
        document.removeEventListener('wheel', preventWheelScroll)
        document.removeEventListener('keydown', preventKeyScroll)
        menuWrapper?.querySelectorAll<HTMLElement>(':scope > ul').forEach((ul) => {
          ul.style.overscrollBehavior = ''
        })
        if (container) container.style.height = ''
        document.documentElement.style.overflowY = ''
        document.documentElement.style.overscrollBehavior = ''
      }
    }
  }, [isOpen, themeName])

  useEffect(() => {
    setIsOpen(false)
  }, [location.pathname])

  useEffect(() => {
    selectedItem.current = findActiveItem(items)
  }, [items])

  const toggleOffCanvas = () => {
    setIsOpen(!isOpen)
    if (isLanguageMenuOpen) setIsLanguageMenuOpen(false)
  }

  const closeOffCanvas = () => {
    setIsOpen(false)
    if (isLanguageMenuOpen) setIsLanguageMenuOpen(false)
  }

  const handlers = useSwipeable({
    onSwipedLeft: direction === 'left' ? closeOffCanvas : undefined,
    onSwipedRight: direction === 'right' ? closeOffCanvas : undefined,
  })

  const accessibilityStyle: CSSProperties = {
    visibility: isHidden ? 'hidden' : undefined,
  }

  return (
    <div className={className}>
      <div className="toggle-menu">
        <button
          className="burger-icon"
          aria-label={t(`${isOpen ? 'closeMainMenuButton' : 'openMainMenuButton'}.accessibilityLabel`)}
          aria-controls="main-menu-offcanvas"
          aria-expanded={isOpen}
          onClick={toggleOffCanvas}
        >
          <span className="burger-icon-stripes" />
        </button>
      </div>
      <div {...handlers} id="main-menu-offcanvas" style={accessibilityStyle}>
        <div className="main-menu-overlay" onClick={closeOffCanvas} />
        <div className="main-menu-wrapper">
          <div className="main-menu-header" />
          {locales.length > 1 && (
            <>
              <button
                className="language-indicator-mobile"
                aria-expanded={isLanguageMenuOpen}
                aria-controls="language-menu-mobile"
                onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
              >
                <span className="globe-icon"></span>
                <span>
                  {isLanguageMenuOpen
                    ? t('mobileMenuLanguageSwitch.label')
                    : t(`:enumerations.languages.${shopLocale.substring(0, 2)}`)}
                </span>
                <span className={cc(['arrow-icon', isLanguageMenuOpen && 'open'])}></span>
              </button>
              <ul id="language-menu-mobile" className={cc(['language-menu-mobile', isLanguageMenuOpen && 'open'])}>
                {locales.map((locale) => (
                  <li key={locale.identifier} className={cc({ selected: locale.identifier === shopLocale })}>
                    <button onClick={() => dispatch(switchLanguage(locale.identifier, location))}>
                      {languageSelectTranslations.get(locale.language)}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          <ul className="main-menu">
            {items.map((item) => (
              <MenuItem key={item.id} item={item} selectedItem={selectedItem.current} />
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

export function findActiveItem(items: Frontend.NestedPage[]) {
  for (const item of items) {
    if (item.isCurrentPage) return item
    if (item.children.length) {
      const foundItem = findActiveItem(item.children)
      if (foundItem) return foundItem
    }
  }
}

export function isMenuItemOpen({
  item,
  selectedItem,
}: {
  item: Frontend.NestedPage
  selectedItem: Frontend.NestedPage
}): boolean {
  return !!(selectedItem && selectedItem.parents?.find(({ id }) => id === item.id))
}
